import React, { useEffect, useState } from "react"
import { IconBrandWhatsapp, IconMessage, IconX } from "@tabler/icons-react"
import LandingButton from "./LandingButton"
import apiClient from "../../lib/api"

const inputClassName =
  "w-full rounded-xl border border-[#d9d9d9] bg-[#fafafa] px-2.5 py-3 font-dm-sans text-base tracking-wide text-[#0d0d0d] placeholder:text-[#787878] outline-none focus:border-dc-primary-900"

const labelClassName = "font-dm-sans text-sm leading-[22px] tracking-wide text-[#2e2e2e]"

type ContactChannel = "whatsapp" | "sms"

type MessageLinks = {
  whatsappUrl: string
  smsUrl: string
}

type Props = {
  open: boolean
  cellGroupId?: string
  cellGroupName?: string
  onClose: () => void
}

const isAppleDevice = () =>
  /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)

const smsUrlForDevice = (smsUrl: string) => (isAppleDevice() ? smsUrl.replace("?body=", "&body=") : smsUrl)

const channelOptionClass = (selected: boolean) =>
  `flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 font-dm-sans text-sm font-semibold tracking-wide transition ${
    selected
      ? "border-dc-primary-900 bg-[#f4f7ff] text-dc-primary-900"
      : "border-[#d9d9d9] bg-white text-[#2e2e2e] hover:border-[#b8b8b8]"
  }`

const LandingCellGroupJoinModal: React.FC<Props> = ({
  open,
  cellGroupId,
  cellGroupName,
  onClose,
}) => {
  const [name, setName] = useState("")
  const [channel, setChannel] = useState<ContactChannel>("whatsapp")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")
  const [messageLinks, setMessageLinks] = useState<MessageLinks | null>(null)
  const [sentChannel, setSentChannel] = useState<ContactChannel>("whatsapp")

  useEffect(() => {
    if (!open) return

    setSubmitStatus("idle")
    setName("")
    setChannel("whatsapp")
    setMessageLinks(null)
    setSentChannel("whatsapp")

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }

    document.addEventListener("keydown", handleEscape)
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [open, onClose])

  if (!open || !cellGroupId) return null

  const openSelectedChannel = (links: MessageLinks, selected: ContactChannel) => {
    if (selected === "sms") {
      window.location.href = smsUrlForDevice(links.smsUrl)
      return
    }

    window.open(links.whatsappUrl, "_blank", "noopener,noreferrer")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      setSubmitStatus("error")
      return
    }

    setIsSubmitting(true)
    setSubmitStatus("idle")

    try {
      const links = await apiClient.createCellGroupInterest({
        cellGroupId,
        name: name.trim(),
        channel,
      })
      setMessageLinks(links)
      setSentChannel(channel)
      openSelectedChannel(links, channel)
      setSubmitStatus("success")
      setName("")
    } catch (err) {
      console.error("Error submitting cell group request:", err)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cell-group-join-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-[0px_2px_12px_0px_rgba(0,0,0,0.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex size-9 items-center justify-center rounded-full text-[#4a4a4a] transition-colors hover:bg-[#f5f5f5]"
        >
          <IconX size={20} stroke={1.5} />
        </button>

        <form onSubmit={handleSubmit} className="flex flex-col gap-7 px-6 py-8">
          <header className="flex flex-col gap-2 pr-8">
            <h2 id="cell-group-join-title" className="font-outfit text-[28px] font-semibold leading-tight text-[#0d0d0d]">
              Join {cellGroupName}
            </h2>
            <p className="font-dm-sans text-base leading-6 tracking-wide text-[#2e2e2e]">
              Leave your name, then send a WhatsApp or a text to the cell leader.
            </p>
          </header>

          {submitStatus !== "success" && (
            <>
              <div className="flex flex-col gap-2">
                <label htmlFor="cell-group-join-name" className={labelClassName}>
                  Your name
                </label>
                <input
                  id="cell-group-join-name"
                  type="text"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className={inputClassName}
                  required
                />
              </div>

              <fieldset className="flex flex-col gap-2">
                <legend className={labelClassName}>Send via</legend>
                <div className="grid grid-cols-2 gap-3">
                  <label className={channelOptionClass(channel === "whatsapp")}>
                    <input
                      type="radio"
                      name="cell-group-join-channel"
                      value="whatsapp"
                      checked={channel === "whatsapp"}
                      onChange={() => setChannel("whatsapp")}
                      className="sr-only"
                    />
                    <IconBrandWhatsapp size={18} stroke={1.5} />
                    WhatsApp
                  </label>
                  <label className={channelOptionClass(channel === "sms")}>
                    <input
                      type="radio"
                      name="cell-group-join-channel"
                      value="sms"
                      checked={channel === "sms"}
                      onChange={() => setChannel("sms")}
                      className="sr-only"
                    />
                    <IconMessage size={18} stroke={1.5} />
                    Text message
                  </label>
                </div>
              </fieldset>
            </>
          )}

          {submitStatus === "error" && (
            <p className="font-dm-sans text-sm text-red-600">
              Please enter your name so we can send the join request.
            </p>
          )}
          {submitStatus === "success" && messageLinks && (
            <div className="flex flex-col gap-3">
              <p className="font-dm-sans text-sm text-[#2e2e2e]">
                {sentChannel === "sms"
                  ? "Your message is ready. Send it as a text if it did not open automatically."
                  : "Your message is ready. Send it in WhatsApp if it did not open automatically."}
              </p>
              {sentChannel === "sms" ? (
                <LandingButton href={smsUrlForDevice(messageLinks.smsUrl)}>Send text</LandingButton>
              ) : (
                <LandingButton href={messageLinks.whatsappUrl}>Open WhatsApp</LandingButton>
              )}
            </div>
          )}

          {submitStatus !== "success" && (
            <div className="flex flex-wrap gap-3">
              <LandingButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Preparing…" : "Send request"}
              </LandingButton>
              <button
                type="button"
                onClick={onClose}
                className="font-dm-sans text-sm font-semibold text-[#5a5a5a] transition hover:text-[#1f1f1f]"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default LandingCellGroupJoinModal
